import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import DialogLoaderButton from "../loader-button/DialogLoaderButton";
import { PartialReceptionCounts } from "@/interfaces/PartialReception";
import {
  CircleAlert,
  CircleCheck,
  CircleX,
  Download,
  Upload,
} from "lucide-react";

interface PartialReceptionConfirmationDialogProps {
  isOpened: boolean;
  setIsOpened: (value: boolean) => void;
  counts: PartialReceptionCounts;
  isLoading: boolean;
  isReceived: boolean;
  handleValidate: () => void;
  handleBLDownload: () => void;
  handleBLJoin: () => void;
}

const PartialReceptionConfirmationDialog: React.FC<
  PartialReceptionConfirmationDialogProps
> = ({
  isOpened,
  setIsOpened,
  counts,
  isLoading,
  isReceived,
  handleValidate,
  handleBLDownload,
  handleBLJoin,
}) => {
  const countsRecap = (
    <div className="grid grid-cols-3 gap-2">
      <div className="flex flex-col items-center rounded-lg border border-emerald-200 bg-emerald-50 py-3">
        <CircleCheck className="h-4 w-4 text-emerald-600" />
        <span className="text-lg font-semibold text-emerald-700">
          {counts.accepted}
        </span>
        <span className="text-xs text-emerald-700">Acceptés</span>
      </div>
      <div className="flex flex-col items-center rounded-lg border border-red-200 bg-red-50 py-3">
        <CircleX className="h-4 w-4 text-red-500" />
        <span className="text-lg font-semibold text-red-600">
          {counts.refused}
        </span>
        <span className="text-xs text-red-600">Refusés</span>
      </div>
      <div className="flex flex-col items-center rounded-lg border border-gray-200 bg-gray-50 py-3">
        <span className="text-lg font-semibold text-gray-700">
          {counts.total}
        </span>
        <span className="text-xs text-gray-600">Total articles</span>
      </div>
    </div>
  );

  return (
    <Dialog
      open={isOpened}
      onOpenChange={(value) => {
        if (isLoading) return;
        setIsOpened(value);
      }}
    >
      <DialogContent>
        {!isReceived ? (
          <>
            <DialogHeader>
              <DialogTitle>Confirmation de la réception</DialogTitle>
              <DialogDescription>
                Vérifiez le récapitulatif avant de valider la réception
                partielle.
              </DialogDescription>
            </DialogHeader>

            {countsRecap}

            <div className="w-full bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm text-amber-700 flex items-start gap-2">
              <CircleAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>
                Les articles non scannés seront automatiquement acceptés.
              </span>
            </div>

            <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
              <Button
                variant="outline"
                className="mr-2"
                onClick={() => setIsOpened(false)}
                disabled={isLoading}
              >
                Annuler
              </Button>
              <DialogLoaderButton
                name="Confirmer la réception"
                handleSubmit={handleValidate}
                isLoading={isLoading}
              />
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Réception validée</DialogTitle>
              <DialogDescription>
                Téléchargez le BL, faites-le cacheter puis joignez-le au retour.
              </DialogDescription>
            </DialogHeader>

            {countsRecap}

            <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
              <Button
                variant="outline"
                className="mr-2"
                onClick={handleBLDownload}
              >
                <Download className="mr-2 h-4 w-4" />
                Télécharger le BL
              </Button>
              <DialogLoaderButton
                name="Joindre le BL cacheté"
                icon={<Upload className="mr-2 h-4 w-4" />}
                handleSubmit={handleBLJoin}
                isLoading={false}
              />
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PartialReceptionConfirmationDialog;
